import {
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { AreaModel } from '../../area.model';
import { AreaDeleteModel } from '../area-delete.model';
import { AreaDeleteUsersAndUnitsOptionsCardComponent } from '../area-delete-users-and-units-options-card/area-delete-users-and-units-options-card.component';
import { IAreaToDeleteData, IAreaToDeleteDataItem } from '../area-to-delete-data.model';
import { AreaDeleteCardBase } from '../area-delete-card-base';
import { AreaDeleteAreaContentOptionsCardComponent } from '../area-delete-area-content-options-card.component/area-delete-area-content-options-card.component';
import { AreaDeleteTransferCardComponent } from '../area-delete-transfer-card/area-delete-transfer-card.component';
import { AreaDeleteUserContentOptionsCardComponent } from '../area-delete-user-content-options-card/area-delete-user-content-options-card.component';
import { areaDeleteIsItemToUnShare } from '../area-delete-utils';
import { createMap } from '@fi-app/shared/utils/create-map';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-area-delete-confirmation-card',
    templateUrl: './area-delete-confirmation-card.component.html',
    styleUrls: ['./area-delete-confirmation-card.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AreaDeleteConfirmationCardComponent extends AreaDeleteCardBase implements OnChanges {
    @Input() area: AreaModel;
    @Input() areaToTransferUnits: AreaModel;
    @Input() areaToTransferContent: AreaModel;
    @Input() areaDeleteModel: AreaDeleteModel;
    @Input() areaToDeleteData: IAreaToDeleteData;
    @Input() option1: string;
    @Input() option2: string;
    @Input() option3: string;
    @Input() optionsCard1: AreaDeleteUsersAndUnitsOptionsCardComponent;
    @Input() optionsCard2: AreaDeleteUserContentOptionsCardComponent;
    @Input() optionsCard3: AreaDeleteAreaContentOptionsCardComponent;
    @Input() transferAreaContentCard: AreaDeleteTransferCardComponent;

    @Output() onConfirm = new EventEmitter();

    signText: string;

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges) {
        this.updateTexts();
    }

    texts = [];

    getUnitsText() {
        const parts = [];

        if (this.areaToDeleteData.BusinessUnitIdsToDelete.length > 0) {
            parts.push(
                this.areaToDeleteData.BusinessUnitIdsToDelete.length +
                    ' ' +
                    this.objectTypes.GetNameByObjectType(
                        ObjectTypes.BusinessUnit,
                        this.areaToDeleteData.BusinessUnitIdsToDelete.length > 1,
                    ),
            );
        }

        if (this.areaToDeleteData.UserIdsToDelete.length > 0) {
            parts.push(
                this.areaToDeleteData.UserIdsToDelete.length +
                    ' ' +
                    this.objectTypes.GetNameByObjectType(
                        ObjectTypes.User,
                        this.areaToDeleteData.UserIdsToDelete.length > 1,
                    ),
            );
        }

        return this.joinSummaryGroup(parts);
    }

    updateTexts() {
        this.texts = [];

        const unitsText = this.getUnitsText();
        if (unitsText) {
            const unitsResultTextId =
                this.option1 === 'transfer'
                    ? 'area_delete.confirmation_transfer_units'
                    : 'area_delete.confirmation_delete_units';
            this.texts.push(
                this.appTextService.get(unitsResultTextId, {
                    '{items}': unitsText,
                }),
            );
        }

        function addItems(items: Array<IAreaToDeleteDataItem>, addItems: Array<IAreaToDeleteDataItem>) {
            const itemsMap = createMap(items, 'ObjectType');
            addItems.forEach((item) => {
                if (!itemsMap.hasOwnProperty(item.ObjectType)) {
                    itemsMap[item.ObjectType] = Object.assign({}, item);
                    items.push(itemsMap[item.ObjectType]);
                } else {
                    itemsMap[item.ObjectType].Count += item.Count;
                }
            });
        }

        let transferOwnershipItems = [],
            deleteItems = [],
            unShareItems = [];

        if (this.optionsCard1.option === 'delete') {
            addItems(
                deleteItems,
                this.areaToDeleteData.ItemsToDeleteByUserUnitRights.filter((item) => !areaDeleteIsItemToUnShare(item)),
            );
            addItems(transferOwnershipItems, this.areaToDeleteData.SharedItemsToChangeOwner);
        }

        if (this.optionsCard2.option === 'transfer') {
            addItems(transferOwnershipItems, this.areaToDeleteData.UnsharedItemsToChangeOwner);
        } else if (this.optionsCard2.option === 'delete') {
            addItems(deleteItems, this.optionsCard2.itemsToDelete);
            addItems(unShareItems, this.optionsCard2.itemsToUnShare);
        }

        if (this.optionsCard3.option === 'delete') {
            addItems(deleteItems, this.optionsCard3.itemsToDelete);
            addItems(unShareItems, this.optionsCard3.itemsToUnShare);
        }

        const self = this;

        function buildGroupNames(items) {
            const parts = items.map(
                (item) => item.Count + ' ' + self.objectTypes.GetNameByObjectType(item.ObjectType, item.Count > 1),
            );

            return self.joinSummaryGroup(parts);
        }

        if (transferOwnershipItems.length > 0) {
            this.texts.push(
                this.appTextService.get('area_delete.confirmation_transfer_ownership', {
                    '{items}': buildGroupNames(transferOwnershipItems),
                }),
            );
        }

        if (deleteItems.length > 0) {
            this.texts.push(
                this.appTextService.get('area_delete.confirmation_delete_units', {
                    '{items}': buildGroupNames(deleteItems),
                }),
            );
        }

        if (unShareItems.length > 0) {
            this.texts.push(
                this.appTextService.get('area_delete.confirmation_unshare', {
                    '{items}': buildGroupNames(unShareItems),
                }),
            );
        }

        if (this.optionsCard3.option === 'transfer') {
            this.texts.push(
                this.appTextService.get('area_delete.confirmation_transfer_content', {
                    '{area_to_delete}': this.area.Name,
                    '{area_to_transfer}': this.transferAreaContentCard.getSelectedAreaName(),
                }),
            );
        }
    }

    confirmClick() {
        this.onConfirm.emit();
    }
}
